import React, { useState, useEffect } from 'react';
import api from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { toast } from 'sonner';
import { LayoutGrid } from 'lucide-react';

export default function HeatmapNotas({ year, trimestre }) {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState({ heatmap: [], colunas: [] });
  const [modo, setModo] = useState('turma_disciplina');
  
  useEffect(() => {
    loadData();
  }, [year, trimestre, modo]);
  
  const loadData = async () => {
    setLoading(true);
    try {
      const params = { year, modo };
      if (trimestre) params.trimestre = trimestre;
      
      const res = await api.get('/central-notas/heatmap', { params });
      setData(res.data);
    } catch (error) {
      console.error('Erro ao carregar heatmap:', error);
      toast.error('Erro ao carregar heatmap');
    } finally {
      setLoading(false);
    }
  };
  
  if (loading) {
    return (
      <Card className="bg-white border border-purple-200/50">
        <CardContent className="py-8">
          <Skeleton className="h-[500px] bg-slate-100" />
        </CardContent>
      </Card>
    );
  }
  
  return (
    <div className="space-y-4" data-testid="heatmap-notas">
      {/* Header */}
      <Card className="bg-white border border-purple-200/50 shadow-sm">
        <CardContent className="py-4">
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-orange-100">
                <LayoutGrid className="w-5 h-5 text-orange-600" />
              </div>
              <div>
                <h3 className="font-semibold text-slate-800">Mapa de Calor</h3>
                <p className="text-sm text-slate-500">Visualização rápida de gargalos acadêmicos</p>
              </div>
            </div>
            
            <div className="flex items-center gap-2">
              <Button
                variant={modo === 'turma_disciplina' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setModo('turma_disciplina')}
                className={modo === 'turma_disciplina' ? 'bg-orange-500 hover:bg-orange-600' : 'border-slate-200'}
              >
                Turma x Disciplina
              </Button>
              <Button
                variant={modo === 'turma_tipo' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setModo('turma_tipo')}
                className={modo === 'turma_tipo' ? 'bg-orange-500 hover:bg-orange-600' : 'border-slate-200'}
              >
                Turma x Tipo
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>
      
      {/* Heatmap */}
      <Card className="bg-white border border-purple-200/50 shadow-sm overflow-hidden">
        <div className="overflow-x-auto dna-responsive-table sticky-first-col">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr>
                <th className="sticky left-0 bg-slate-50 px-4 py-3 text-left font-semibold text-slate-700 min-w-[150px]">
                  Turma
                </th>
                {data.colunas?.map((col) => (
                  <th key={col.id} className="px-2 py-3 text-center font-semibold text-slate-700 min-w-[70px]">
                    <span className="truncate block max-w-[80px] text-xs" title={col.nome}>
                      {col.nome?.length > 10 ? col.nome.substring(0, 10) + '...' : col.nome}
                    </span>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {data.heatmap?.length === 0 ? (
                <tr>
                  <td colSpan={(data.colunas?.length || 0) + 1} className="px-4 py-12 text-center text-slate-500">
                    Nenhum dado encontrado
                  </td>
                </tr>
              ) : (
                data.heatmap?.map((row) => (
                  <tr key={row.id} className="border-b border-slate-100">
                    <td className="sticky left-0 bg-white px-4 py-2 font-medium text-slate-800 text-xs">
                      {row.nome}
                    </td>
                    {row.celulas?.map((celula) => (
                      <td 
                        key={celula.col_id}
                        className="px-2 py-2 text-center"
                        style={{ 
                          backgroundColor: celula.valor !== null ? `${celula.color}30` : '#F8FAFC',
                        }}
                      >
                        {celula.valor !== null ? (
                          <span 
                            className="font-bold text-sm"
                            style={{ color: celula.color }}
                            title={`${celula.col_nome}: ${celula.valor}`}
                          >
                            {celula.valor?.toFixed(1)}
                          </span>
                        ) : (
                          <span className="text-slate-300 text-xs">-</span>
                        )}
                      </td>
                    ))}
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </Card>
      
      {/* Legenda */}
      <div className="flex items-center justify-center gap-8 text-sm">
        <div className="flex items-center gap-2">
          <div className="w-6 h-6 rounded" style={{ backgroundColor: '#10B98130' }} />
          <span className="text-slate-600">&ge; 8.0 Excelente</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-6 h-6 rounded" style={{ backgroundColor: '#3B82F630' }} />
          <span className="text-slate-600">6.0 - 7.9 Satisfatorio</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-6 h-6 rounded" style={{ backgroundColor: '#EF444430' }} />
          <span className="text-slate-600">&lt; 6.0 Critico</span>
        </div>
      </div>
    </div>
  );
}
